import { ReactComponentWithRef } from "@tamagui/core";
import { createStyledContext, isWeb } from "@tamagui/web";
import React from "react";
import {
  GetProps,
  RadioGroup as TamaguiRadioGroup,
  XStack,
  styled,
  withStaticProperties,
} from "tamagui";

import { Label } from "../Typography";
import withTracking from "../withTracking";
import FormField, { type FormFieldProps } from "./FormField";
import { useFormFieldAria } from "./useFormFieldAria";
import { useFormFieldIds } from "./useFormFieldIds";

type RadioGroupContextT = {
  value?: string;
  borderless?: boolean;
  disabled?: boolean;
};
export const RadioGroupContext = createStyledContext<RadioGroupContextT>({
  value: undefined,
  borderless: false,
  disabled: false,
});

const RadioGroupItemFrame = styled(XStack, {
  name: "RadioGroup",
  alignItems: "center",
  paddingLeft: "$150",
  borderColor: "$strokeNeutral",
  borderWidth: "$strokeWidth",
  borderRadius: "$150",
  justifyContent: "center",
  gap: "$50",
  variants: {
    borderless: {
      true: {
        padding: 0,
        borderWidth: 0,
        gap: "$100",
      },
      false: {
        backgroundColor: "$fillModal",
      },
    },
  },
});

const StyledRadioGroupItem = styled(TamaguiRadioGroup.Item, {
  size: "$600",
  tabIndex: "0",
  marginVertical: "$150",
  hitSlop: 10,
  cursor: "pointer",
  backgroundColor: "transparent",
  borderColor: "$iconBase",
  hoverStyle: {
    backgroundColor: "transparent",
    borderColor: "$iconBase",
  },
  focusStyle: {
    backgroundColor: "transparent",
    borderColor: "$fillPrimary",
  },
  pressStyle: {
    backgroundColor: "$fillPrimary",
    borderColor: "$fillPrimary",
  },
  outlineOffset: 2,
  variants: {
    active: {
      true: {
        backgroundColor: "$fillPrimary",
        borderColor: "$fillPrimary",
        focusStyle: {
          backgroundColor: "$fillPrimary",
        },
        hoverStyle: {
          backgroundColor: "$fillPrimary",
          borderColor: "$fillPrimary",
        },
      },
    },
    borderless: {
      true: {
        marginVertical: 0,
        backgroundColor: "$fillModal",
      },
      false: {},
    },
  },
});

const StyledRadioGroupIndicator = styled(TamaguiRadioGroup.Indicator, {
  backgroundColor: "$fillModal",
  height: "$100",
  width: "$100",
});

const RadioGroupItem: ReactComponentWithRef<
  GetProps<typeof TamaguiRadioGroup.Item> & { startIcon?: React.ReactNode },
  HTMLButtonElement
> = React.forwardRef(
  ({ id, value, children, testID, startIcon, disabled: itemDisabled }, ref) => {
    const {
      value: activeValue,
      borderless,
      disabled: groupDisabled,
    } = React.useContext<RadioGroupContextT>(RadioGroupContext);
    const disabled = groupDisabled || itemDisabled;
    const isActive = activeValue === value;

    const frameProps: any = {};
    const itemProps: any = {};
    if (!isWeb) {
      frameProps.role = "radio";
      frameProps["aria-checked"] = isActive;
      frameProps["aria-disabled"] = disabled || undefined;
      frameProps.accessible = true;
      itemProps.role = undefined;
    }

    return (
      <RadioGroupItemFrame {...frameProps} borderless={borderless}>
        <StyledRadioGroupItem
          {...itemProps}
          disabled={disabled}
          borderless={borderless}
          // After borderless, so the selected fill wins over the borderless fill
          active={isActive}
          id={String(id)}
          value={value}
          ref={ref}
          testID={testID}
        >
          <StyledRadioGroupIndicator />
        </StyledRadioGroupItem>
        {startIcon}
        <Label
          htmlFor={String(id)}
          normal
          size="$sm"
          flex={1}
          cursor="pointer"
          {...(!borderless && { padding: "$150" })}
        >
          {children}
        </Label>
      </RadioGroupItemFrame>
    );
  },
);
RadioGroupItem.displayName = "RadioGroupItem";

const RadioGroupFrame = styled(TamaguiRadioGroup, {
  context: RadioGroupContext,
  gap: "$150",
  // Declared so borderless feeds the context instead of reaching the DOM
  variants: {
    borderless: { true: {}, false: {} },
  },
});

const RadioGroupRoot = RadioGroupFrame.styleable<FormFieldProps>(
  ({ label, errorText, rootProps, labelProps, id, ...props }, ref) => {
    const { fieldId, labelId, errorId } = useFormFieldIds(id);
    const formFieldAria = useFormFieldAria({
      hasLabel: Boolean(label),
      labelId,
      errorId,
      errorText,
    });
    return (
      <FormField
        fieldId={fieldId}
        label={label}
        errorText={errorText}
        rootProps={rootProps}
        labelProps={labelProps}
      >
        <RadioGroupFrame id={fieldId} {...formFieldAria} ref={ref} {...props} />
      </FormField>
    );
  },
);

export const RadioGroup = withStaticProperties(
  withTracking(RadioGroupRoot, "radioGroup"),
  {
    Item: RadioGroupItem,
    StyledItem: StyledRadioGroupItem,
    StyledIndicator: StyledRadioGroupIndicator,
    Props: RadioGroupContext.Provider,
  },
);
